import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  Button,
  Separator as Divider,
  SettingsIcon as Settings,
  Typography,
} from "@iluvatar/ui-kit";
// libraries
import dayjs, { extend } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
extend(relativeTime);

import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";

// hooks
import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";

// components
import { FlexSpacer } from "@/components/FlexSpacer";
import { ProfileDescription } from "@/components/ProfileDescription";
import { ProfileImageBanner } from "@/components/ProfileImageBanner";
import { ImageCarousel } from "@/components/ProfileImageBanner/ImageCarousel";
import { TagList } from "@/components/TagList";
import DefaultLayout from "@/layout/default-page";

// types
import { useCurrentUser } from "@/hooks/useCurrentUser";

const UserHistoryPage: React.FC = () => {
  const params: { userId: string } = useParams({ strict: false });
  const navigator = useNavigate();
  const isMedia = useMediaQuery("only screen and (max-width: 468px)");
  const { uid } = useAuthenticationContext();
  const { user } = useCurrentUser();

  const isAdmin = uid === params.userId;

  return (
    <DefaultLayout
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] flex start-start relative pb-12"
      }
    >
      {!isMedia ? (
        <ProfileImageBanner
          onOrderChange={() => {}}
          isAdmin={false}
          images={
            user?.profiles.default.imageIds
              ?.map((id) => ({
                ...(user?.profiles.default.imagesById![id] || {}),
                id: id,
              }))
              .filter((ref) => !ref.metadata.isProcessing) || []
          }
          className="mt-12"
        />
      ) : (
        <ImageCarousel
          communityId="default"
          userId={uid || ""}
          images={
            user?.profiles.default.imageIds
              ?.map((id) => ({
                ...(user?.profiles.default.imagesById![id] || {}),
                id: id,
              }))
              .filter((ref) => !ref.metadata.isProcessing) || []
          }
        />
      )}
      {user && (
        <Typography variant="h3" className="flex flex-row">
          {user?.profiles.default.displayName}
          {isAdmin && <FlexSpacer />}
          {isAdmin && (
            <Settings
              onClick={() =>
                navigator({
                  to: "/user/$userId/edit",
                  params: {
                    userId: params.userId,
                  },
                })
              }
              className="mb-1 ml-5 opacity-80 hover:opacity-100 clickable cursor-pointer"
            />
          )}
        </Typography>
      )}
      {user && (
        <Typography variant="subtitle2">
          {user?.about.birthDate
            ? `${dayjs(new Date()).diff(user.about.birthDate, "years")}, `
            : ""}
          {[user.about.gender, user.about.sexuality].join(" ")}
        </Typography>
      )}
      {user?.profiles.default.subTitle && (
        <Typography variant="subtitle2">
          {user.profiles.default.subTitle}
        </Typography>
      )}
      <ProfileDescription>
        {user?.profiles.default.description}
      </ProfileDescription>
      <TagList
        items={user?.profiles.default.interests || []}
        label="Interests"
      />
      <TagList items={user?.profiles.default.desires || []} label="Desires" />
      <TagList
        items={user?.profiles.default.characteristics || []}
        label="Characteristics"
      />
      <Divider />
      <Button
        variant="outline"
        disabled
        className={["mt-8", "[color:rgb(var(--uik-info))]"]
          .filter(Boolean)
          .join(" ")}
      >
        Report Account
      </Button>
    </DefaultLayout>
  );
};

export const Route = createFileRoute("/user/$userId/")({
  component: UserHistoryPage,
});
